import { CadCommandType } from '../../../command/CadCommandType';
import type { FeatureReferenceBinding } from '../../binding/FeatureReferenceBinding';
import { createFeatureReferenceBinding } from '../../../utils/feature/createFeatureReferenceBinding';
import { ChamferFeatureDefinitionModel } from '../../definition/chamfer/ChamferFeatureDefinition';
import { ChamferFeaturePrototype } from '../../prototype/chamfer/ChamferFeaturePrototype';
import { FeatureCommandAdapter, type FeatureCommandBuildContext } from '../FeatureCommandAdapter';
import { buildChamferModelTopologyBindings } from '../../binding/buildChamferModelTopologyBindings';

/**
 * 倒角命令适配器。
 */
export class ChamferCommandAdapter extends FeatureCommandAdapter<ChamferFeaturePrototype> {
    /**
     * @inheritdoc
     */
    public supports(command: CadCommand): boolean {
        return command.type === CadCommandType.CHAMFER;
    }

    /**
     * @inheritdoc
     */
    public createPrototypeFromCommand(command: CadCommand): ChamferFeaturePrototype | null {
        if (!this.supports(command)) {
            return null;
        }

        const params = structuredClone(command.params as ChamferParams);
        const definition = ChamferFeatureDefinitionModel.fromParams({
            commandParams: params,
        });
        const bindings = this.buildBindingsFromParams(definition.getCommandParams());
        return new ChamferFeaturePrototype({
            featureId: command.id,
            definition,
            bindings,
            sourceSketchId: null,
        });
    }

    /**
     * @inheritdoc
     */
    public createCommandFromPrototype(
        context: FeatureCommandBuildContext<ChamferFeaturePrototype>,
    ): CadCommand {
        const commandParams = context.prototype.getDefinition().getCommandParams();
        return {
            id: context.prototype.getFeatureId(),
            type: CadCommandType.CHAMFER,
            clientId: context.clientId,
            timestamp: context.timestamp ?? Date.now(),
            params: {
                ...commandParams,
                featureKind: 'chamfer',
                blendMode: 'chamfer',
                referenceBindings: context.prototype.getBindings().map((binding) => binding.serialize()),
            },
        };
    }

    /**
     * 从命令参数恢复输入绑定。
     *
     * @param params 命令参数。
     * @returns 输入绑定集合。
     */
    private buildBindingsFromParams(params: ChamferParams): FeatureReferenceBinding[] {
        const serializedBindings = buildChamferModelTopologyBindings({
            upstreamBodyObjectId: params.upstreamBodyObjectId,
            definition: params.definition,
        });
        return serializedBindings.map((binding) => createFeatureReferenceBinding(binding));
    }
}
